import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { retrieveUsers } from "../slices/userSlice";
import { retrieveUserGroups } from "../slices/userGroupSlice";
import { filterObjectsByProperty } from "../utils/dataUtils";
import { debugLog, errorLog } from "../utils/debugUtils";
import { isAdmin, isDeveloper, isStrat, isDesigner } from "../utils/sessionUtils";

// Create Context
const LookupsContext = createContext();

// Provider Component
export const LookupsProvider = ({ children }) => {
	const dispatch = useDispatch();

	// Synchronously load lookups initially
	const initialLookups = sessionStorage.hasOwnProperty("lookups")
		? JSON.parse(sessionStorage.getItem("lookups"))
		: {};

	const [allUsers, setAllUsers] = useState([]);
	const [allUserGroups, setAllUserGroups] = useState([]);
	const [allAdmins, setAllAdmins] = useState([]);
	const [allDevelopers, setAllDevelopers] = useState([]);
	const [allStrats, setAllStrats] = useState([]);
	const [allDesigners, setAllDesigners] = useState([]);
	const [lookups, setLookups] = useState(initialLookups);
	const [sortedCfgTypes, setSortedCfgTypes] = useState([]);
	const [allUserRoles, setAllUserRoles] = useState([]);

	// Load lookups from sessionStorage
	const loadLookups = useCallback(() => {
		if (sessionStorage.hasOwnProperty("lookups")) {
			const updatedLookups = JSON.parse(sessionStorage.getItem("lookups"));
			setLookups(updatedLookups);
			setAllUserRoles(updatedLookups.USER_ROLES);

			debugLog(2, "[LookupsProvider][FLOW] loadLookups from sessionStorage", { lookups: updatedLookups });

			return updatedLookups;
		}
		return {};
	}, []);

	// Fetch users and groups from database
	const loadUsersAndGroups = useCallback(async () => {
		let usersCount = 0;
		let userGroupsCount = 0;

		await dispatch(retrieveUsers())
			.unwrap()
			.then((response) => {
				setAllUsers(response);
				usersCount = response?.length ?? 0;
				setAllAdmins(response.filter(isAdmin)); // filter to Admins only
				setAllDevelopers(response.filter(isDeveloper)); // filter to Developers only
				setAllDesigners(response.filter(isDesigner)); // filter to Designers only
				setAllStrats(response.filter(isStrat)); // filter to Strategy Team users onlysetAllDesigners(designers);
			})
			.catch((error) => {
				errorLog(2, "[LookupsProvider][FLOW] retrieveUsers failed", { error: error });
			});

		await dispatch(retrieveUserGroups())
			.unwrap()
			.then((response) => {
				setAllUserGroups(response);
				userGroupsCount = response?.length ?? 0;
			})
			.catch((error) => {
				errorLog(2, "[LookupsProvider][FLOW] retrieveUserGroups failed", { error: error });
			});

		debugLog(3, "[LookupsProvider][STATE] users and groups loaded", {
			usersCount,
			userGroupsCount,
		});
	}, [dispatch]);

	const loginUserId = useSelector((state) => state.session?.loginUser?.id);

	// Reload everything
	const reloadLookups = useCallback(() => {
		if (loginUserId) {
			loadLookups();
			loadUsersAndGroups();
		}
	}, [loadLookups, loadUsersAndGroups, loginUserId]);

	// Initial load
	useEffect(() => {
		if (loginUserId) {
			loadLookups();
			loadUsersAndGroups();
		}
	}, [loadLookups, loadUsersAndGroups, loginUserId]);

	// Update sortedCfgTypes when lookups change
	const updateSortedCfgTypes = useCallback(
		(groupId) => {
			if (lookups.BL_LOOKUPS_ALL && groupId) {
				const groupCfgs = filterObjectsByProperty(lookups.BL_LOOKUPS_ALL, "group_id", groupId);
				setSortedCfgTypes(groupCfgs.slice().sort((a, b) => a.sort_id - b.sort_id));
			}
		},
		[lookups]
	);

	return (
		<LookupsContext.Provider
			value={{
				lookups,
				allUsers,
				allAdmins,
				allDevelopers,
				allStrats,
				allDesigners,
				allUserGroups,
				allUserRoles,
				sortedCfgTypes,
				updateSortedCfgTypes,
				reloadLookups, // Trigger reload on demand
			}}
		>
			{children}
		</LookupsContext.Provider>
	);
};

// Hook for components to use
export const useLookupsData = () => {
	const context = useContext(LookupsContext);

	if (!context) {
		throw new Error("useLookupsData must be used within a LookupsProvider");
	}
	return context;
};
